/**
 * Illustrative Cadenza exchanges for the homepage preview.
 *
 * Not transcripts of real client projects, and the numbers are invented. The
 * tool names, however, are the real ones Cadenza exposes over MCP — keep them
 * accurate, since they are the part a technical reader will recognise and check.
 *
 * `answer` is an array of segments: plain strings render as-is, `{ em }`
 * renders emphasised. Authored this way so the component never needs innerHTML.
 *
 * `scope` is the OAuth scope that exchange genuinely requires — reading is
 * `mcp:read`, anything that writes needs `mcp:engineering.write`. Shown in the
 * demo's header so the tiered-permission model is visible rather than implied.
 */

export const CADENZA_EXCHANGES = [
  {
    chip: "Why did July recovery dip?",
    scope: "mcp:read",
    question: "Recovery came in under forecast in July — what does the data say?",
    calls: ["list_operational_logs", "query_operational_data", "query_results"],
    answer: [
      "Modelled recovery over July was ",
      { em: "8.2%" },
      " against ",
      { em: "6.9%" },
      " actual. Across the same window your Daily Pad Operations log has irrigation averaging ",
      { em: "7.9 L/h/m²" },
      " where the scenario assumes ",
      { em: "9.6" },
      " — and the daily shortfall tracks the under-irrigated days almost one for one. The four days below 7 L/h/m² line up with the steepest divergence.",
    ],
    evidence: [
      { label: "Modelled", value: "8.2%" },
      { label: "Actual", value: "6.9%", down: true },
      { label: "Irrigation vs plan", value: "−18%", down: true },
    ],
  },
  {
    chip: "What's costing us downtime?",
    scope: "mcp:read",
    question: "Which assets cost the most downtime this quarter, and what happened?",
    calls: ["list_maintenance_assets", "list_maintenance_logs"],
    answer: [
      { em: "Booster Pump A" },
      " accounts for ",
      { em: "14.5 of the 23 hours" },
      " logged this quarter — two mechanical seal failures and a discharge-pressure problem still open from yesterday. Everything else on the irrigation network totals under ",
      { em: "4 hours" },
      ". The seal kit has now been replaced twice in six weeks, which is worth treating as a pattern rather than two incidents.",
    ],
    evidence: [
      { label: "Logged downtime", value: "23 h" },
      { label: "Worst asset", value: "Booster Pump A" },
      { label: "Still open", value: "2", down: true },
    ],
  },
  {
    chip: "Compare baseline vs optimisation",
    scope: "mcp:read",
    question: "How does the optimisation scenario differ from baseline, and did it pay off?",
    calls: ["list_scenarios", "compare_scenario_configs", "compare_simulations"],
    answer: [
      "The optimisation run raises Pad 3 irrigation by ",
      { em: "12%" },
      " and shortens the rest cycle from ",
      { em: "10 days to 7" },
      "; every other input matches baseline. Across the two simulations that is worth ",
      { em: "+3.4%" },
      " cumulative copper by day 900, but peak PLS flow lands ",
      { em: "8% higher" },
      " — close enough to your stated plant ceiling to be worth checking before committing.",
    ],
    evidence: [
      { label: "Cum. recovery", value: "+3.4%" },
      { label: "Peak PLS", value: "+8%" },
      { label: "Config deltas", value: "2" },
    ],
  },
  {
    chip: "Build a leach schedule",
    scope: "mcp:engineering.write",
    question: "Take the March stack plan I just sent and turn it into a leach schedule.",
    calls: ["get_heap_layout", "duplicate_scenario", "set_leach_schedule"],
    answer: [
      "Registered the drawing against the heap grid, matched ",
      { em: "16 panels" },
      " to ",
      { em: "505 cells" },
      ", and wrote ",
      { em: "515 irrigation intervals" },
      " at 7.3 mm/h into a duplicate of your baseline. Each interval is clipped to that cell's stacked-to-buried window, so cells stop leaching once they are covered. The new scenario is tagged ",
      { em: "✦ AI" },
      " so it is obvious what to review before you run it.",
    ],
    evidence: [
      { label: "Panels read", value: "16" },
      { label: "Cells scheduled", value: "505" },
      { label: "Intervals", value: "515" },
    ],
  },
];
